import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { date, money } from '@ui/lib/format';
import { Badge, EmptyState, ErrorState, PageHead, Pager, Stat, TableSkeleton } from '@ui/components/ui';
import { api, qs } from '../lib/api';

interface InvoiceRow {
  id: string;
  number: string;
  total: string;
  status: string;
  issuedAt: string;
  dueAt: string;
  paidAt: string | null;
  tenant: { id: string; slug: string; name: string };
}

interface InvoicePage {
  items: InvoiceRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  totals: { amount: number };
}

export function Invoices() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'' | 'open' | 'paid'>('');
  const [page, setPage] = useState(1);

  const query = { status: status || undefined, page };

  const list = useQuery({
    queryKey: ['invoices', query],
    queryFn: () => api<InvoicePage>(`/invoices${qs(query)}`),
  });

  const pay = useMutation({
    mutationFn: (id: string) => api(`/invoices/${id}/pay`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['summary'] });
    },
  });

  const open = list.data?.items.filter((i) => i.status === 'open') ?? [];
  const overdue = open.filter((i) => new Date(i.dueAt) < new Date());

  return (
    <div className="page">
      <PageHead title="فواتير الاشتراكات" subtitle="ما صدر على المستأجرين وما حُصِّل منه" />

      <div className="stats">
        <Stat label="عدد الفواتير" value={list.data?.total ?? '—'} />
        <Stat
          label="الإجمالي"
          value={<span className="num">{list.data ? money(list.data.totals.amount) : '—'}</span>}
          note={status ? 'ضمن التصفية الحالية' : 'كل الفواتير'}
        />
        <Stat label="مفتوحة" value={open.length || '—'} note="في الصفحة الحالية" />
        <Stat
          label="متأخرة"
          value={<span className={overdue.length ? 'neg' : ''}>{overdue.length || '—'}</span>}
          note="تجاوزت الاستحقاق"
        />
      </div>

      <div className="toolbar">
        <div className="row" style={{ gap: 2 }}>
          {[
            { key: '', label: 'الكل' },
            { key: 'open', label: 'مفتوحة' },
            { key: 'paid', label: 'مسدَّدة' },
          ].map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${status === s.key ? ' btn-primary' : ''}`}
              onClick={() => {
                setStatus(s.key as '' | 'open' | 'paid');
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الرقم</th>
                <th>المستأجر</th>
                <th>الإصدار</th>
                <th>الاستحقاق</th>
                <th>القيمة</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((i) => {
                  const late = i.status === 'open' && new Date(i.dueAt) < new Date();
                  return (
                    <tr key={i.id}>
                      <td className="num cell-strong">{i.number}</td>
                      <td>
                        <Link to={`/tenants/${i.tenant.id}`} className="link">
                          {i.tenant.name}
                        </Link>
                        <div className="tiny mute num">{i.tenant.slug}</div>
                      </td>
                      <td className="num cell-mute">{date(i.issuedAt)}</td>
                      <td className={`num ${late ? 'neg' : 'cell-mute'}`}>{date(i.dueAt)}</td>
                      <td className="num">{money(i.total)}</td>
                      <td>
                        <Badge kind={i.status === 'paid' ? 'success' : late ? 'danger' : 'info'}>
                          {i.status === 'paid' ? 'مسدَّدة' : late ? 'متأخرة' : 'مفتوحة'}
                        </Badge>
                      </td>
                      <td className="end">
                        {i.status === 'open' && (
                          <button
                            className="btn btn-sm"
                            onClick={() => pay.mutate(i.id)}
                            disabled={pay.isPending}
                          >
                            تسجيل سداد
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا فواتير"
            text="تُصدر فواتير الاشتراك من صفحة كل مستأجر."
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}
